import React, { useState, useEffect, useRef } from 'react';
import { X, Video, Users, Settings, Mic, MicOff, VideoOff, Phone, MessageSquare, Share2, Monitor, Volume2, VolumeX } from 'lucide-react';

const ClaraVideoModal = ({ isOpen, onClose, meeting, roomName }) => {
  const [isJitsiLoaded, setIsJitsiLoaded] = useState(false);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [participantCount, setParticipantCount] = useState(1);
  const [chatVisible, setChatVisible] = useState(false);
  const jitsiContainerRef = useRef(null);
  const jitsiApiRef = useRef(null);

  // Generate room name if not provided
  const finalRoomName = roomName || `clara-video-${Date.now()}`;
  const displayName = meeting?.title || 'Clara Video Meeting';

  useEffect(() => {
    if (isOpen && jitsiContainerRef.current) {
      loadJitsiMeet();
    }

    return () => {
      if (jitsiApiRef.current) {
        jitsiApiRef.current.dispose();
        jitsiApiRef.current = null;
      }
    };
  }, [isOpen]);

  const loadJitsiMeet = () => {
    // Load Jitsi Meet API if not already loaded
    if (!window.JitsiMeetExternalAPI) {
      const script = document.createElement('script');
      script.src = 'https://video.clara360.de/external_api.js';
      script.onload = () => initializeJitsi();
      document.head.appendChild(script);
    } else {
      initializeJitsi();
    }
  };

  const initializeJitsi = () => {
    if (jitsiApiRef.current) {
      jitsiApiRef.current.dispose();
    }

    const options = {
      roomName: finalRoomName,
      width: '100%',
      height: '100%',
      parentNode: jitsiContainerRef.current,
      configOverwrite: {
        startWithAudioMuted: false,
        startWithVideoMuted: false,
        enableWelcomePage: false,
        enableClosePage: false,
        prejoinPageEnabled: false,
        disableDeepLinking: true,
        defaultLanguage: 'de',
        // Clara360 Branding
        brandingDataUrl: '',
        brandingRoomAlias: 'Clara Video',
        // UI Customization
        toolbarButtons: [
          'microphone', 'camera', 'closedcaptions', 'desktop', 'fullscreen',
          'fodeviceselection', 'hangup', 'profile', 'chat', 'recording',
          'livestreaming', 'etherpad', 'sharedvideo', 'settings', 'raisehand',
          'videoquality', 'filmstrip', 'invite', 'feedback', 'stats', 'shortcuts',
          'tileview', 'videobackgroundblur', 'download', 'help', 'mute-everyone'
        ]
      },
      interfaceConfigOverwrite: {
        BRAND_WATERMARK_LINK: '',
        SHOW_BRAND_WATERMARK: false,
        SHOW_JITSI_WATERMARK: false,
        SHOW_POWERED_BY: false,
        SHOW_PROMOTIONAL_CLOSE_PAGE: false,
        SHOW_WATERMARK_FOR_GUESTS: false,
        DEFAULT_BACKGROUND: '#1a1a1a',
        // Clara360 Colors
        TOOLBAR_BUTTONS: [
          'microphone', 'camera', 'closedcaptions', 'desktop', 'fullscreen',
          'fodeviceselection', 'hangup', 'profile', 'chat', 'recording',
          'livestreaming', 'etherpad', 'sharedvideo', 'settings', 'raisehand',
          'videoquality', 'filmstrip', 'invite', 'feedback', 'stats', 'shortcuts',
          'tileview', 'videobackgroundblur', 'download', 'help', 'mute-everyone'
        ],
        SETTINGS_SECTIONS: ['devices', 'language', 'moderator', 'profile', 'calendar'],
        VIDEO_LAYOUT_FIT: 'both'
      },
      userInfo: {
        displayName: 'Clara360 User',
        email: ''
      }
    };

    try {
      jitsiApiRef.current = new window.JitsiMeetExternalAPI('video.clara360.de', options);
      
      // Event listeners
      jitsiApiRef.current.addEventListener('videoConferenceJoined', () => {
        setIsJitsiLoaded(true);
        console.log('Clara Video: Conference joined');
      });

      jitsiApiRef.current.addEventListener('participantJoined', (participant) => {
        setParticipantCount(prev => prev + 1);
        console.log('Clara Video: Participant joined', participant);
      });

      jitsiApiRef.current.addEventListener('participantLeft', (participant) => {
        setParticipantCount(prev => Math.max(1, prev - 1));
        console.log('Clara Video: Participant left', participant);
      });

      jitsiApiRef.current.addEventListener('audioMuteStatusChanged', (event) => {
        setIsAudioMuted(event.muted);
      });

      jitsiApiRef.current.addEventListener('videoMuteStatusChanged', (event) => {
        setIsVideoMuted(event.muted);
      });

      jitsiApiRef.current.addEventListener('screenSharingStatusChanged', (event) => {
        setIsScreenSharing(event.on);
      });

      jitsiApiRef.current.addEventListener('readyToClose', () => {
        onClose();
      });

    } catch (error) {
      console.error('Clara Video: Failed to initialize Jitsi', error);
    }
  };

  const toggleAudio = () => {
    if (jitsiApiRef.current) {
      jitsiApiRef.current.executeCommand('toggleAudio');
    }
  };

  const toggleVideo = () => {
    if (jitsiApiRef.current) {
      jitsiApiRef.current.executeCommand('toggleVideo');
    }
  };

  const toggleScreenShare = () => {
    if (jitsiApiRef.current) {
      jitsiApiRef.current.executeCommand('toggleShareScreen');
    }
  };

  const toggleChat = () => {
    if (jitsiApiRef.current) {
      jitsiApiRef.current.executeCommand('toggleChat');
      setChatVisible(!chatVisible);
    }
  };

  const hangUp = () => {
    if (jitsiApiRef.current) {
      jitsiApiRef.current.executeCommand('hangup');
    }
    onClose();
  };

  const copyRoomLink = () => {
    const roomUrl = `https://video.clara360.de/${finalRoomName}`;
    navigator.clipboard.writeText(roomUrl).then(() => {
      alert('Meeting-Link kopiert!');
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center">
      <div className="w-full h-full max-w-7xl max-h-screen bg-white rounded-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-pink-600 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Video className="w-6 h-6" />
              <h2 className="text-xl font-bold">🎥 Clara Video</h2>
            </div>
            <div className="hidden md:flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{participantCount} Teilnehmer</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span>Live</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={copyRoomLink}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              title="Meeting-Link kopieren"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              title="Schließen"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Video Container */}
        <div className="flex-1 relative bg-gray-900">
          {!isJitsiLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-white">
              <div className="text-center">
                <Video className="w-16 h-16 mx-auto mb-4 animate-pulse text-red-500" />
                <h3 className="text-xl font-semibold mb-2">Clara Video wird geladen...</h3>
                <p className="text-gray-400">Verbindung zu {displayName}</p>
                <div className="mt-4">
                  <div className="w-48 h-2 bg-gray-700 rounded-full mx-auto overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-red-500 to-pink-500 rounded-full animate-pulse"></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div 
            ref={jitsiContainerRef} 
            className="w-full h-full"
            style={{ minHeight: '400px' }}
          />
        </div>

        {/* Control Bar */}
        <div className="bg-gray-800 text-white p-4">
          <div className="flex items-center justify-center gap-4">
            {/* Audio Control */}
            <button
              onClick={toggleAudio}
              className={`p-3 rounded-full transition-colors ${
                isAudioMuted 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-gray-600 hover:bg-gray-700'
              }`}
              title={isAudioMuted ? 'Mikrofon einschalten' : 'Mikrofon ausschalten'}
            >
              {isAudioMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>

            {/* Video Control */}
            <button
              onClick={toggleVideo}
              className={`p-3 rounded-full transition-colors ${
                isVideoMuted 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-gray-600 hover:bg-gray-700'
              }`}
              title={isVideoMuted ? 'Kamera einschalten' : 'Kamera ausschalten'}
            >
              {isVideoMuted ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
            </button>

            {/* Screen Share */}
            <button
              onClick={toggleScreenShare}
              className={`p-3 rounded-full transition-colors ${
                isScreenSharing 
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : 'bg-gray-600 hover:bg-gray-700'
              }`}
              title={isScreenSharing ? 'Bildschirm-Freigabe beenden' : 'Bildschirm teilen'}
            >
              <Monitor className="w-5 h-5" />
            </button>

            {/* Chat */}
            <button
              onClick={toggleChat}
              className={`p-3 rounded-full transition-colors ${
                chatVisible 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-gray-600 hover:bg-gray-700'
              }`}
              title="Chat öffnen/schließen"
            >
              <MessageSquare className="w-5 h-5" />
            </button>

            {/* Hang Up */}
            <button
              onClick={hangUp}
              className="p-3 bg-red-600 hover:bg-red-700 rounded-full transition-colors ml-4"
              title="Meeting verlassen"
            >
              <Phone className="w-5 h-5 transform rotate-135" />
            </button>
          </div>

          {/* Meeting Info */}
          <div className="mt-3 text-center text-sm text-gray-400">
            <p>Meeting: {displayName}</p>
            <p className="text-xs">Room: {finalRoomName}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaraVideoModal;

